import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { getAppPortalRoot } from "../utils/appPortal";
import { X } from "lucide-react";
import { API_URL } from "../config/api";
import {
  CAPTURE_DATE_MASK,
  DEFAULT_REGISTER_FILTERS,
  EWSS_GRADES,
  EWSS_THRESHOLD_OPERATORS,
  GROUPEMENT_MODE_OPTIONS,
  ILLUSTRATION_FILTER_OPTIONS,
  WORD_ORIGIN_FILTER_OPTIONS,
  toGroupementThemeOptions,
  maskCaptureDateInput,
  maskTimeInput,
  NO_EWSS_GAME_COUNT_OPTIONS,
  PAQUET_FILTER_OPTIONS,
  PLAYED_COUNT_FILTER_MAX,
  POTION_AID_FILTER_OPTIONS,
  REGISTER_CONSECUTIVE_FAIL_OPTIONS,
  sanitizeRegisterFilters,
  sanitizeRegisterPaquets,
  TIME_MASK,
} from "../constants/registerFilters";
import PackAssignDropdown from "./PackAssignDropdown";
import "./DeleteWordModal.css";
import "./RegisterFilterModal.css";

const FILTER_MENU_Z = 2147483600;
const FILTER_MENU_MAX_HEIGHT = 200;

const EMPTY_OPTIONS = {
  auteurs: [],
  livres: [],
  years: [],
  byLivre: {},
  byAuteur: {},
  groupements: [],
};

function sortFr(values = []) {
  return [...values].sort((a, b) => a.localeCompare(b, "fr", { sensitivity: "base" }));
}

function toDropdownOptions(entries, { emptyValue = "", emptyLabel } = {}) {
  const options = entries.map((entry) =>
    typeof entry === "string"
      ? { value: entry, label: entry }
      : { value: entry.value, label: entry.label }
  );
  if (emptyLabel != null) {
    return [{ value: emptyValue, label: emptyLabel }, ...options];
  }
  return options;
}

function reconcileLinkedFilters(draft, options) {
  const next = { ...draft };
  const byLivre = options.byLivre || {};
  const byAuteur = options.byAuteur || {};

  if (next.livre && byLivre[next.livre]) {
    next.auteur = byLivre[next.livre].auteur || next.auteur || "";
    return next;
  }

  if (next.auteur && byAuteur[next.auteur]) {
    const authorLivres = byAuteur[next.auteur].livres || [];
    if (next.livre && !authorLivres.includes(next.livre)) {
      next.livre = "";
    }
  }

  return next;
}

function FilterField({ label, children, full = false }) {
  return (
    <div
      className={`register-filter-modal__field${
        full ? " register-filter-modal__field--full" : ""
      }`}
    >
      <span className="register-filter-modal__label">{label}</span>
      {children}
    </div>
  );
}

function FilterCheck({ checked, onChange, children, className = "" }) {
  return (
    <label
      className={`register-filter-modal__checkbox${className ? ` ${className}` : ""}`}
    >
      <input
        type="checkbox"
        className="register-filter-modal__checkbox-input"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="register-filter-modal__checkbox-marker" aria-hidden="true" />
      <span className="register-filter-modal__checkbox-label">{children}</span>
    </label>
  );
}

function FilterDropdown({
  value,
  onChange,
  options,
  disabled = false,
  ariaLabel,
  menuMaxHeight = FILTER_MENU_MAX_HEIGHT,
}) {
  return (
    <PackAssignDropdown
      options={options}
      value={value}
      onChange={onChange}
      disabled={disabled}
      ariaLabel={ariaLabel}
      matchTriggerWidth
      openBelow
      menuMaxHeight={menuMaxHeight}
      menuZIndex={FILTER_MENU_Z}
      menuClassName="register-filter-modal__dropdown-menu"
    />
  );
}

function FilterNumberInput({
  value,
  onChange,
  disabled = false,
  placeholder,
  "aria-label": ariaLabel,
}) {
  return (
    <input
      type="number"
      className="register-filter-modal__input"
      value={value}
      min={0}
      max={PLAYED_COUNT_FILTER_MAX}
      step={1}
      inputMode="numeric"
      placeholder={placeholder}
      aria-label={ariaLabel}
      disabled={disabled}
      onChange={onChange}
    />
  );
}

function FilterDateInput({ value, onChange, "aria-label": ariaLabel }) {
  return (
    <input
      type="text"
      className="register-filter-modal__input register-filter-modal__date-input"
      value={value}
      inputMode="numeric"
      placeholder={CAPTURE_DATE_MASK}
      maxLength={CAPTURE_DATE_MASK.length}
      aria-label={ariaLabel}
      onChange={(event) => onChange(maskCaptureDateInput(event.target.value, value))}
    />
  );
}

function FilterTimeInput({ value, onChange, "aria-label": ariaLabel }) {
  return (
    <input
      type="text"
      className="register-filter-modal__input register-filter-modal__time-input"
      value={value}
      inputMode="numeric"
      placeholder={TIME_MASK}
      maxLength={TIME_MASK.length}
      aria-label={ariaLabel}
      onChange={(event) => onChange(maskTimeInput(event.target.value))}
    />
  );
}

function FilterRangeClear({ onClick, disabled, ariaLabel }) {
  return (
    <button
      type="button"
      className="register-filter-modal__range-clear"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      <X size={14} strokeWidth={2.4} aria-hidden="true" />
    </button>
  );
}

export default function RegisterFilterModal({ open, value, onChange, onClose }) {
  const [draft, setDraft] = useState(DEFAULT_REGISTER_FILTERS);
  const [options, setOptions] = useState(EMPTY_OPTIONS);
  const [loadingOptions, setLoadingOptions] = useState(false);
  // Opérateur de seuil conservé indépendamment de la note (sinon il retombe sur "eq"
  // tant qu'aucune note n'est choisie).
  const [ewssOperator, setEwssOperator] = useState("eq");

  useEffect(() => {
    if (!open) return;
    const merged = { ...DEFAULT_REGISTER_FILTERS, ...value };
    if (merged.noyauOnly === "yes") {
      merged.includeNoyau = "yes";
    }
    if (merged.paquet && !merged.paquets?.length) {
      merged.paquets = sanitizeRegisterPaquets(merged.paquet);
    } else {
      merged.paquets = sanitizeRegisterPaquets(merged.paquets);
    }
    delete merged.noyauOnly;
    delete merged.paquet;
    setDraft(sanitizeRegisterFilters(merged));
    setEwssOperator(
      merged.ewssGrade
        ? "eq"
        : merged.ewssStrictBelowGrade
        ? "lt"
        : merged.ewssBelowGrade
        ? "lte"
        : merged.ewssStrictAboveGrade
        ? "gt"
        : merged.ewssAboveGrade
        ? "gte"
        : "eq"
    );
  }, [open, value]);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setLoadingOptions(true);

    fetch(`${API_URL}/mots-liste-detail/filter-options`)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || "Impossible de charger les options.");
        if (!cancelled) setOptions({ ...EMPTY_OPTIONS, ...data });
      })
      .catch(() => {
        if (!cancelled) setOptions(EMPTY_OPTIONS);
      })
      .finally(() => {
        if (!cancelled) setLoadingOptions(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open || loadingOptions) return;
    setDraft((prev) => reconcileLinkedFilters(prev, options));
  }, [open, loadingOptions, options]);

  const livreSelected = Boolean(draft.livre);

  const availableLivres = useMemo(() => {
    if (livreSelected) {
      return options.livres || [];
    }
    if (draft.auteur && options.byAuteur?.[draft.auteur]?.livres) {
      return options.byAuteur[draft.auteur].livres;
    }
    return options.livres || [];
  }, [draft.auteur, draft.livre, livreSelected, options]);

  const lockedAuteur = livreSelected
    ? options.byLivre?.[draft.livre]?.auteur || draft.auteur
    : draft.auteur;

  const updateDraft = (patch) => {
    setDraft((prev) => {
      let next = { ...prev, ...patch };

      if (patch.noEwssGameCount && patch.noEwssGameCount !== "") {
        next.ewssGrade = "";
        next.ewssBelowGrade = "";
        next.ewssAboveGrade = "";
        next.ewssStrictBelowGrade = "";
        next.ewssStrictAboveGrade = "";
      }

      if ("livre" in patch) {
        if (patch.livre) {
          const meta = options.byLivre?.[patch.livre];
          if (meta?.auteur) {
            next.auteur = meta.auteur;
          }
        } else {
          next.auteur = "";
        }
      }

      if ("auteur" in patch && patch.auteur && !next.livre) {
        const authorLivres = options.byAuteur?.[patch.auteur]?.livres || [];
        if (next.livre && !authorLivres.includes(next.livre)) {
          next.livre = "";
        }
      }

      return reconcileLinkedFilters(next, options);
    });
  };

  const togglePaquet = (value, checked) => {
    setDraft((prev) => {
      const current = sanitizeRegisterPaquets(prev.paquets);
      const paquets = checked
        ? [...new Set([...current, value])]
        : current.filter((entry) => entry !== value);
      return reconcileLinkedFilters({ ...prev, paquets }, options);
    });
  };

  const togglePoolFlag = (key, checked) => {
    setDraft((prev) => ({
      ...prev,
      [key]: checked ? "yes" : "",
    }));
  };

  const ewssGradeValue =
    draft.ewssGrade ||
    draft.ewssStrictBelowGrade ||
    draft.ewssBelowGrade ||
    draft.ewssStrictAboveGrade ||
    draft.ewssAboveGrade ||
    "";

  const applyEwssThreshold = (operator, grade) => {
    setEwssOperator(operator);
    const patch = {
      ewssGrade: "",
      ewssBelowGrade: "",
      ewssAboveGrade: "",
      ewssStrictBelowGrade: "",
      ewssStrictAboveGrade: "",
    };
    if (grade) {
      patch.noEwssGameCount = "";
      if (operator === "eq") patch.ewssGrade = grade;
      else if (operator === "lt") patch.ewssStrictBelowGrade = grade;
      else if (operator === "lte") patch.ewssBelowGrade = grade;
      else if (operator === "gt") patch.ewssStrictAboveGrade = grade;
      else patch.ewssAboveGrade = grade;
    }
    updateDraft(patch);
  };

  if (!open) return null;

  const handleApply = () => {
    const next = sanitizeRegisterFilters(reconcileLinkedFilters(draft, options));
    onChange?.(next);
    onClose?.();
  };

  const handleReset = () => {
    onChange?.({ ...DEFAULT_REGISTER_FILTERS });
    onClose?.();
  };

  return createPortal(
    <div
      className="delete-word-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div
        className="delete-word-modal register-filter-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="register-filter-modal-title"
      >
        <button
          type="button"
          className="delete-word-modal__close"
          onClick={onClose}
          aria-label="Fermer"
        >
          <X size={18} strokeWidth={2.5} />
        </button>

        <h3 id="register-filter-modal-title" className="register-filter-modal__title">
          Filtrer le registre
        </h3>
        <p className="delete-word-modal__text register-filter-modal__intro">
          Combinez les critères ci-dessous pour affiner la liste des mots enregistrés.
        </p>

        <div className="register-filter-modal__body">
          <div className="register-filter-modal__sections">
            <FilterField label="Seuil" full>
              <div className="register-filter-modal__threshold">
                <FilterDropdown
                  value={ewssOperator}
                  disabled={Boolean(draft.noEwssGameCount)}
                  ariaLabel="Opérateur de seuil EWSS"
                  options={EWSS_THRESHOLD_OPERATORS}
                  onChange={(next) => applyEwssThreshold(next, ewssGradeValue)}
                />
                <FilterDropdown
                  value={ewssGradeValue}
                  disabled={Boolean(draft.noEwssGameCount)}
                  ariaLabel="Note EWSS"
                  options={toDropdownOptions(EWSS_GRADES, {
                    emptyLabel: "— Toutes —",
                  })}
                  onChange={(next) => applyEwssThreshold(ewssOperator, next)}
                />
              </div>
            </FilterField>

            <FilterField label="Sans score EWSS">
              <FilterDropdown
                value={draft.noEwssGameCount}
                disabled={Boolean(ewssGradeValue)}
                ariaLabel="Filtrer les mots sans score EWSS"
                options={NO_EWSS_GAME_COUNT_OPTIONS}
                onChange={(next) => updateDraft({ noEwssGameCount: next })}
              />
            </FilterField>

            <FilterField label="Dernière parution">
              <FilterCheck
                checked={draft.lastAppearanceFailed === "yes"}
                onChange={(checked) =>
                  updateDraft({ lastAppearanceFailed: checked ? "yes" : "" })
                }
              >
                Mal restitués à la dernière parution
              </FilterCheck>
            </FilterField>

            <FilterField label="Échecs successifs" full>
              <div className="register-filter-modal__consecutive-fails">
                <FilterCheck
                  className="register-filter-modal__checkbox--consecutive"
                  checked={draft.consecutiveFails === "yes"}
                  onChange={(checked) =>
                    updateDraft({ consecutiveFails: checked ? "yes" : "" })
                  }
                >
                  Filtrer par échecs successifs
                </FilterCheck>
                <div
                  className={`register-filter-modal__consecutive-fails-select${
                    draft.consecutiveFails === "yes"
                      ? ""
                      : " register-filter-modal__choice-dropdown--hidden"
                  }`}
                >
                  <FilterDropdown
                    value={String(draft.consecutiveFailsCount)}
                    disabled={draft.consecutiveFails !== "yes"}
                    ariaLabel="Nombre d'échecs successifs"
                    options={REGISTER_CONSECUTIVE_FAIL_OPTIONS.map((count) => ({
                      value: String(count),
                      label: `${count} échecs successifs`,
                    }))}
                    onChange={(next) =>
                      updateDraft({ consecutiveFailsCount: Number(next) })
                    }
                  />
                </div>
              </div>
            </FilterField>

            <FilterField label="Nombre de fois joués">
              <div className="register-filter-modal__played-count">
                <FilterNumberInput
                  value={draft.playedCountMin}
                  placeholder="Min"
                  aria-label="Minimum de parties jouées"
                  onChange={(event) => updateDraft({ playedCountMin: event.target.value })}
                />
                <span className="register-filter-modal__played-count-sep">à</span>
                <FilterNumberInput
                  value={draft.playedCountMax}
                  placeholder="Max"
                  aria-label="Maximum de parties jouées"
                  onChange={(event) => updateDraft({ playedCountMax: event.target.value })}
                />
              </div>
            </FilterField>

            <FilterField label="Illustration">
              <FilterDropdown
                value={draft.illustration}
                ariaLabel="Filtrer par illustration"
                options={ILLUSTRATION_FILTER_OPTIONS}
                onChange={(next) => updateDraft({ illustration: next })}
              />
            </FilterField>

            <FilterField label="Regroupement thématique" full>
              <div className="register-filter-modal__groupement">
                <div className="register-filter-modal__groupement-mode">
                  <FilterDropdown
                    value={draft.groupement}
                    ariaLabel="Filtrer les mots affiliés à un regroupement"
                    options={GROUPEMENT_MODE_OPTIONS}
                    onChange={(next) =>
                      updateDraft({
                        groupement: next,
                        ...(next !== "only" ? { groupementId: "" } : {}),
                      })
                    }
                  />
                </div>
                {draft.groupement === "only" ? (
                  <div className="register-filter-modal__groupement-theme">
                    <FilterDropdown
                      value={draft.groupementId}
                      disabled={loadingOptions}
                      ariaLabel="Choisir une thématique"
                      options={toGroupementThemeOptions(options.groupements)}
                      onChange={(next) => updateDraft({ groupementId: next })}
                    />
                  </div>
                ) : null}
              </div>
            </FilterField>

            <FilterField label="Potions utilisées" full>
              <div className="register-filter-modal__potions-used">
                <FilterCheck
                  checked={draft.potionsUsed === "yes"}
                  onChange={(checked) =>
                    updateDraft({ potionsUsed: checked ? "yes" : "" })
                  }
                >
                  Filtrer par potion utilisée
                </FilterCheck>
                <div
                  className={`register-filter-modal__potions-used-select${
                    draft.potionsUsed === "yes"
                      ? ""
                      : " register-filter-modal__choice-dropdown--hidden"
                  }`}
                >
                  <FilterDropdown
                    value={draft.potionsUsedFilter}
                    disabled={draft.potionsUsed !== "yes"}
                    ariaLabel="Potion utilisée"
                    options={POTION_AID_FILTER_OPTIONS}
                    onChange={(next) => updateDraft({ potionsUsedFilter: next })}
                  />
                </div>
              </div>
            </FilterField>

            <div className="register-filter-modal__field register-filter-modal__field--full">
              <div className="register-filter-modal__pools-head">
                <span className="register-filter-modal__label">Pools de mots</span>
                <div className="register-filter-modal__word-origin-select">
                  <FilterDropdown
                    value={draft.wordOrigin || ""}
                    ariaLabel="Origine des mots"
                    options={WORD_ORIGIN_FILTER_OPTIONS}
                    onChange={(next) => updateDraft({ wordOrigin: next })}
                  />
                </div>
              </div>
              <div className="register-filter-modal__pool-grid">
                <FilterCheck
                  checked={draft.includeNoyau === "yes"}
                  onChange={(checked) => togglePoolFlag("includeNoyau", checked)}
                >
                  Noyau actif
                </FilterCheck>
                <FilterCheck
                  checked={draft.includeFavoris === "yes"}
                  onChange={(checked) => togglePoolFlag("includeFavoris", checked)}
                >
                  Mots favoris
                </FilterCheck>
                <FilterCheck
                  checked={draft.includePrioritaires === "yes"}
                  onChange={(checked) => togglePoolFlag("includePrioritaires", checked)}
                >
                  Mots prioritaires
                </FilterCheck>
                {PAQUET_FILTER_OPTIONS.map(({ value, label }) => (
                  <FilterCheck
                    key={value}
                    checked={draft.paquets.includes(value)}
                    onChange={(checked) => togglePaquet(value, checked)}
                  >
                    {label}
                  </FilterCheck>
                ))}
              </div>
            </div>

            <FilterField label="Livre">
              <FilterDropdown
                value={draft.livre}
                disabled={loadingOptions}
                ariaLabel="Filtrer par livre"
                menuMaxHeight={170}
                options={toDropdownOptions(availableLivres, {
                  emptyLabel: "— Tous les livres —",
                })}
                onChange={(next) => updateDraft({ livre: next })}
              />
            </FilterField>

            <FilterField label="Auteur">
              <FilterDropdown
                value={lockedAuteur || ""}
                disabled={loadingOptions || livreSelected}
                ariaLabel="Filtrer par auteur"
                menuMaxHeight={170}
                options={toDropdownOptions(
                  livreSelected && lockedAuteur ? [lockedAuteur] : sortFr(options.auteurs),
                  { emptyLabel: "— Tous les auteurs —" }
                )}
                onChange={(next) => updateDraft({ auteur: next })}
              />
            </FilterField>

            <FilterField label="Date de capture" full>
              <div className="register-filter-modal__capture-range">
                <span className="register-filter-modal__capture-range-label">Entre le</span>
                <FilterDateInput
                  value={draft.captureDateStart}
                  aria-label="Date de capture de début"
                  onChange={(next) => updateDraft({ captureDateStart: next })}
                />
                <span className="register-filter-modal__capture-range-label">et le</span>
                <FilterDateInput
                  value={draft.captureDateEnd}
                  aria-label="Date de capture de fin"
                  onChange={(next) => updateDraft({ captureDateEnd: next })}
                />
                <FilterRangeClear
                  ariaLabel="Vider les dates de capture"
                  disabled={!draft.captureDateStart && !draft.captureDateEnd}
                  onClick={() =>
                    updateDraft({ captureDateStart: "", captureDateEnd: "" })
                  }
                />
              </div>
            </FilterField>

            <FilterField label="Date de jeu" full>
              <div className="register-filter-modal__capture-range register-filter-modal__play-range">
                <span className="register-filter-modal__capture-range-label">Entre le</span>
                <FilterDateInput
                  value={draft.playDateStart}
                  aria-label="Date de jeu de début"
                  onChange={(next) => updateDraft({ playDateStart: next })}
                />
                <FilterTimeInput
                  value={draft.playTimeStart}
                  aria-label="Heure de jeu de début"
                  onChange={(next) => updateDraft({ playTimeStart: next })}
                />
                <span className="register-filter-modal__capture-range-label">et le</span>
                <FilterDateInput
                  value={draft.playDateEnd}
                  aria-label="Date de jeu de fin"
                  onChange={(next) => updateDraft({ playDateEnd: next })}
                />
                <FilterTimeInput
                  value={draft.playTimeEnd}
                  aria-label="Heure de jeu de fin"
                  onChange={(next) => updateDraft({ playTimeEnd: next })}
                />
                <FilterRangeClear
                  ariaLabel="Vider les dates et heures de jeu"
                  disabled={
                    !draft.playDateStart &&
                    !draft.playDateEnd &&
                    !draft.playTimeStart &&
                    !draft.playTimeEnd
                  }
                  onClick={() =>
                    updateDraft({
                      playDateStart: "",
                      playDateEnd: "",
                      playTimeStart: "",
                      playTimeEnd: "",
                    })
                  }
                />
              </div>
            </FilterField>
          </div>
        </div>

        <div className="delete-word-modal__actions register-filter-modal__actions">
          <button
            type="button"
            className="delete-word-modal__btn delete-word-modal__btn--cancel"
            onClick={handleReset}
          >
            Réinitialiser
          </button>
          <button
            type="button"
            className="delete-word-modal__btn delete-word-modal__btn--cancel"
            onClick={onClose}
          >
            Annuler
          </button>
          <button
            type="button"
            className="delete-word-modal__btn delete-word-modal__btn--confirm"
            onClick={handleApply}
          >
            Appliquer
          </button>
        </div>
      </div>
    </div>,
    getAppPortalRoot()
  );
}
